const JPEG_DATA_URL_PREFIX = "data:image/jpeg;base64,";
const PNG_DATA_URL_PREFIX = "data:image/png;base64,";

export const getImageSize = (src: string) => {
  if (
    src.startsWith(JPEG_DATA_URL_PREFIX) ||
    src.startsWith(PNG_DATA_URL_PREFIX)
  ) {
    try {
      const base64Data = src.split(",")[1];
      const binaryData = atob(base64Data);
      const uint8Array = Uint8Array.from(binaryData, (c) => c.charCodeAt(0));
      return src.startsWith(JPEG_DATA_URL_PREFIX)
        ? getJpegDimensions(uint8Array)
        : getPngDimensions(uint8Array);
    } catch (ex) {
      console.error("getImageSize error", ex);
    }
  }
  return null;
};

export function getJpegDimensions(data: Uint8Array) {
  // Check for JPEG SOI marker (0xFFD8)
  if (data.length < 2 || data[0] !== 0xff || data[1] !== 0xd8) {
    console.error("Invalid JPEG: Missing SOI marker");
    return null;
  }

  let offset = 2; // Start after SOI

  while (offset < data.length) {
    // Skip non-marker bytes
    if (data[offset] !== 0xff) {
      offset++;
      continue;
    }

    const marker = data[offset + 1];
    offset += 2; // Move past marker bytes

    // Skip filler bytes
    if (marker === 0xff) {
      continue;
    }

    // Check for critical markers
    if (marker === 0xda) break; // SOS (start of scan)
    if (marker === 0xd9) break; // EOI (end of image)

    // Skip standalone markers
    if ((marker >= 0xd0 && marker <= 0xd7) || marker === 0x01) {
      continue;
    }

    // Read segment length
    if (offset + 1 >= data.length) break;
    const len = (data[offset] << 8) | data[offset + 1];
    if (len < 2 || offset + len > data.length) break;

    // Check for SOF markers (0xC0 - 0xCF)
    if (marker >= 0xc0 && marker <= 0xcf) {
      // Validate segment size (must be at least 8 bytes including length)
      if (len < 8) throw new Error("Invalid SOF segment");

      // Extract dimensions (big-endian)
      const height = (data[offset + 3] << 8) | data[offset + 4];
      const width = (data[offset + 5] << 8) | data[offset + 6];
      return { width, height };
    }

    offset += len; // Skip to next segment
  }

  console.error("JPEG dimensions not found");
  return null;
}

export function getPngDimensions(u8: Uint8Array) {
  // Verify PNG signature (first 8 bytes)
  const signature = [137, 80, 78, 71, 13, 10, 26, 10];
  if (u8.length < 24 || !signature.every((byte, i) => u8[i] === byte)) {
    console.error("Invalid PNG signature");
    return null;
  }

  // Verify IHDR chunk identifier (bytes 12-15)
  const ihdrIdentifier = [73, 72, 68, 82]; // "IHDR" in ASCII
  if (!ihdrIdentifier.every((val, i) => u8[12 + i] === val)) {
    console.error("First chunk is not IHDR");
    return null;
  }

  // Extract dimensions from bytes 16-23
  return {
    width: (u8[16] << 24) | (u8[17] << 16) | (u8[18] << 8) | u8[19],
    height: (u8[20] << 24) | (u8[21] << 16) | (u8[22] << 8) | u8[23]
  };
}
